import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQueueCounts } from '../hooks/useQueueCounts';

function isExpirationKindSubmission(submission: any, task?: any): boolean {
  const taskKind = task?.taskKind || submission?.task?.taskKind;
  const informationType = task?.informationType || submission?.task?.informationType || submission?.informationType;
  return taskKind === 'expiration' || informationType === 'Expiration Date';
}

type TimeFilter = 'today' | '7days' | 'month' | 'all';

interface ChartDataPoint {
  date: string;
  submissions: number;
  approvals: number;
  labels: number;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { counts } = useQueueCounts();
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('7days');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Dashboard — Pixano';
  }, []);
  
  // Stats
  const [totalUsers, setTotalUsers] = useState(0);
  const [newUsers, setNewUsers] = useState(0);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  
  // Tasks needing a labeling schema (task-level, matches the Schema Needed page)
  const [schemaNeededTasks, setSchemaNeededTasks] = useState(0);

  // Marketplace counts
  const [mkt, setMkt] = useState({
    rawPhotoIn: 0,
    rawPhotoOut: 0,
    annotatedIn: 0,
    annotatedOut: 0,
    rawVideoIn: 0,
    rawVideoOut: 0,
  });

  // Calculate time range based on filter
  const getTimeRange = (filter: TimeFilter) => {
    const now = new Date();
    switch (filter) {
      case 'today':
        return { start: new Date(now.setHours(0, 0, 0, 0)), end: new Date() };
      case '7days':
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return { start: sevenDaysAgo, end: new Date() };
      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return { start: monthAgo, end: new Date() };
      case 'all':
        return { start: null, end: null };
      default:
        return { start: null, end: null };
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const timeRange = getTimeRange(timeFilter);
        const startTimestamp = timeRange.start ? Timestamp.fromDate(timeRange.start) : null;
        const endTimestamp = timeRange.end ? Timestamp.fromDate(timeRange.end) : null;

        // Load users (for new users count within time filter)
        let usersQuery;
        if (timeRange.start) {
          usersQuery = query(collection(db, 'users'), where('createdAt', '>=', startTimestamp));
        } else {
          usersQuery = query(collection(db, 'users'));
        }
        const usersSnapshot = await getDocs(usersQuery);
        const allUsers = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt || new Date(),
        }));

        // Total users (all time)
        const allUsersQuery = query(collection(db, 'users'));
        const allUsersSnapshot = await getDocs(allUsersQuery);
        setTotalUsers(allUsersSnapshot.docs.length);

        // New users (within time filter)
        if (timeRange.start) {
          const newUsersCount = allUsers.filter(user => {
            const userDate = user.createdAt instanceof Date ? user.createdAt : new Date(user.createdAt);
            return userDate >= timeRange.start!;
          }).length;
          setNewUsers(newUsersCount);
        } else {
          setNewUsers(allUsers.length);
        }

        // Load submissions
        // Note: Firestore doesn't allow multiple range queries on same field, so we filter end date client-side
        let submissionsQuery;
        if (timeRange.start) {
          submissionsQuery = query(
            collection(db, 'submissions'),
            where('createdAt', '>=', startTimestamp)
          );
        } else {
          submissionsQuery = query(collection(db, 'submissions'));
        }
        const submissionsSnapshot = await getDocs(submissionsQuery);
        let allSubmissions = submissionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt || new Date(),
          completedAt: doc.data().completedAt?.toDate?.() || doc.data().completedAt,
          approvedAt: doc.data().approvedAt?.toDate?.() || doc.data().approvedAt,
          labels: doc.data().labels || [],
        }));

        // Filter by end date client-side if needed
        if (timeRange.end) {
          allSubmissions = allSubmissions.filter(sub => {
            const subDate = sub.createdAt instanceof Date ? sub.createdAt : new Date(sub.createdAt);
            return subDate <= timeRange.end!;
          });
        }

        // Total submissions (within time filter)
        setTotalSubmissions(allSubmissions.length);

        // Build chart data
        const chartDataMap = new Map<string, { submissions: number; approvals: number; labels: number }>();
        
        allSubmissions.forEach(sub => {
          const date = sub.createdAt instanceof Date 
            ? sub.createdAt.toISOString().split('T')[0]
            : new Date(sub.createdAt).toISOString().split('T')[0];
          
          if (!chartDataMap.has(date)) {
            chartDataMap.set(date, { submissions: 0, approvals: 0, labels: 0 });
          }
          
          const dayData = chartDataMap.get(date)!;
          dayData.submissions += 1;
          
          // Count approvals (submissions with approvedAt or completedAt)
          if (sub.approvedAt || sub.completedAt) {
            dayData.approvals += 1;
          }
          
          // Count labels
          const labelCount = Array.isArray(sub.labels) ? sub.labels.length : 0;
          dayData.labels += labelCount;
        });

        const sortedChartData = Array.from(chartDataMap.entries())
          .map(([date, data]) => ({ date, ...data }))
          .sort((a, b) => a.date.localeCompare(b.date))
          .map(item => ({
            ...item,
            date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          }));

        setChartData(sortedChartData);

        // ---- Marketplace counts (always all-time; not affected by the time filter) ----
        // Load tasks map (needed for expiration-kind detection, same as the packages pages)
        const tasksSnapshot = await getDocs(collection(db, 'tasks'));
        const tasksMap: Record<string, any> = {};
        tasksSnapshot.docs.forEach((d) => {
          tasksMap[d.id] = { ...d.data(), id: d.id };
        });

        // Tasks needing a schema — mirrors taskNeedsSchema() in src/pages/labeling/Queue.tsx
        const needsSchema = (t: any) => {
          if (t?.detection?.enabled && t?.detection?.schemaRef?.id) return false;
          if (t?.labelSchemaRef?.id) return false;
          return true;
        };

        // Raw Photos — mirrors PhotoPackages.tsx eligibility
        const mktPhotoSnap = await getDocs(
          query(collection(db, 'submissions'), where('taskType', '==', 'Photo'))
        );
        let rawPhotoIn = 0;
        let rawPhotoOut = 0;
        mktPhotoSnap.docs.forEach((d) => {
          const sub: any = { ...d.data(), id: d.id };
          const isPhotoReady =
            sub.status === 'approved' || sub.status === 'completed' || sub.status === 'labeled';
          const isPhotoNeedsRelabeling = sub.status === 'needs_relabeling';
          if (!isPhotoReady && !isPhotoNeedsRelabeling) return;
          if (isExpirationKindSubmission(sub, tasksMap[sub.taskId])) {
            const pe = sub.packagingEligibility || {};
            if (!pe.photoPackagesEligible || sub.itemTypeStage !== 'final') return;
          }
          if (sub.marketplace?.rawPhoto) rawPhotoIn += 1;
          else rawPhotoOut += 1;
        });

        // Annotated Photos — mirrors DatasetPackages.tsx eligibility
        const mktDatasetSnap = await getDocs(
          query(collection(db, 'submissions'), where('status', 'in', ['completed', 'approved']))
        );
        let annotatedIn = 0;
        let annotatedOut = 0;
        mktDatasetSnap.docs.forEach((d) => {
          const sub: any = { ...d.data(), id: d.id };
          if (sub.isDeleted === true) return;
          if (isExpirationKindSubmission(sub, tasksMap[sub.taskId])) {
            const pe = sub.packagingEligibility || {};
            if (!pe.datasetPackagesEligible) return;
          }
          let eligible = false;
          if (sub.status === 'completed') {
            eligible = true;
          } else if (sub.status === 'approved') {
            eligible = sub.qaAction === 'final_approval' || sub.qaFinalReviewReviewedAt != null;
          }
          if (!eligible) return;
          if (sub.marketplace?.annotatedPhoto) annotatedIn += 1;
          else annotatedOut += 1;
        });

        // Distinct tasks that have approved submissions waiting on a schema
        // (mirrors the Schema Needed page's "Tasks Needing Schema" card)
        const schemaTaskIds = new Set<string>();
        mktDatasetSnap.docs.forEach((d) => {
          const sub: any = d.data();
          if (sub.status !== 'approved') return;
          const t = tasksMap[sub.taskId];
          if (t && needsSchema(t)) schemaTaskIds.add(sub.taskId);
        });
        setSchemaNeededTasks(schemaTaskIds.size);

        // Raw Videos — mirrors VideoPackages.tsx eligibility
        const mktVideoSnap = await getDocs(
          query(collection(db, 'submissions'), where('taskType', '==', 'Video'))
        );
        let rawVideoIn = 0;
        let rawVideoOut = 0;
        mktVideoSnap.docs.forEach((d) => {
          const sub: any = { ...d.data(), id: d.id };
          if (!sub.trimmedVideoUrl) return;
          if (sub.status !== 'approved' && sub.status !== 'completed') return;
          if (sub.marketplace?.rawVideo) rawVideoIn += 1;
          else rawVideoOut += 1;
        });

        setMkt({ rawPhotoIn, rawPhotoOut, annotatedIn, annotatedOut, rawVideoIn, rawVideoOut });

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [timeFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex gap-2">
          {(['today', '7days', 'month', 'all'] as TimeFilter[]).map((filter) => (
            <button
              key={filter}
              onClick={() => setTimeFilter(filter)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timeFilter === filter
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter === '7days' ? '7 Days' : filter === 'all' ? 'All Time' : filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* First Row: Total Users, New Users, Total Submissions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          type="button"
          onClick={() => navigate('/user-data')}
          className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="text-2xl font-bold text-blue-600">{totalUsers.toLocaleString()}</div>
          <div className="text-sm text-gray-600 mt-1">Total Users</div>
        </button>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center">
          <div className="text-2xl font-bold text-green-600">{newUsers.toLocaleString()}</div>
          <div className="text-sm text-gray-600 mt-1">New Users</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center">
          <div className="text-2xl font-bold text-purple-600">{totalSubmissions.toLocaleString()}</div>
          <div className="text-sm text-gray-600 mt-1">Total Submissions</div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Activity Over Time</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
            <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
            <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#f9fafb' }} labelStyle={{ color: '#f9fafb' }} itemStyle={{ color: '#f9fafb' }} />
            <Legend />
            <Line type="monotone" dataKey="submissions" stroke="#8884d8" name="Submissions" />
            <Line type="monotone" dataKey="approvals" stroke="#82ca9d" name="Approvals" />
            <Line type="monotone" dataKey="labels" stroke="#ffc658" name="Labels" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Work Flow Stats */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold text-gray-900">Work Flow Stats</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/approvals')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
          >
            <div className="text-2xl font-bold text-red-600">{counts.approvals}</div>
            <div className="text-sm text-gray-600 mt-1">Task Approvals</div>
          </button>
          <button
            onClick={() => navigate('/labeling/workbench')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
          >
            <div className="text-2xl font-bold text-blue-600">{counts.workbench}</div>
            <div className="text-sm text-gray-600 mt-1">Workbench</div>
          </button>
          <button
            onClick={() => navigate('/labeling/review')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
          >
            <div className="text-2xl font-bold text-green-600">{counts.review}</div>
            <div className="text-sm text-gray-600 mt-1">Review</div>
          </button>
          <button
            onClick={() => navigate('/qa')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
          >
            <div className="text-2xl font-bold text-orange-600">{counts.qa}</div>
            <div className="text-sm text-gray-600 mt-1">QA</div>
          </button>
          <button
            onClick={() => navigate('/schema-needed')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
          >
            <div className="text-2xl font-bold text-yellow-600">{schemaNeededTasks.toLocaleString()}</div>
            <div className="text-sm text-gray-600 mt-1">Tasks Needing Schema</div>
          </button>
          <button
            onClick={() => navigate('/dataset-requests')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
          >
            <div className="text-2xl font-bold text-purple-600">{counts.datasetRequestsCount}</div>
            <div className="text-sm text-gray-600 mt-1">Dataset Requests</div>
          </button>
          <button
            onClick={() => navigate('/support-requests')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
          >
            <div className="text-2xl font-bold text-indigo-600">{counts.supportRequestsCount}</div>
            <div className="text-sm text-gray-600 mt-1">Support Requests</div>
          </button>
          <button
            onClick={() => navigate('/payouts')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-center"
          >
            <div className="text-2xl font-bold text-pink-600">{counts.payoutRequestsCount}</div>
            <div className="text-sm text-gray-600 mt-1">Payout Requests</div>
          </button>
        </div>
      </div>

      {/* Market Place */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold text-gray-900">Market Place</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/client/photo-packages')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-left"
          >
            <div className="text-lg font-semibold text-gray-900 mb-4">Raw Photos</div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">In Market Place:</span>
                <span className="text-sm font-medium text-green-600">{mkt.rawPhotoIn.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Not in Market Place:</span>
                <span className="text-sm font-medium text-gray-900">{mkt.rawPhotoOut.toLocaleString()}</span>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/client/dataset-packages')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-left"
          >
            <div className="text-lg font-semibold text-gray-900 mb-4">Annotated Photos</div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">In Market Place:</span>
                <span className="text-sm font-medium text-green-600">{mkt.annotatedIn.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Not in Market Place:</span>
                <span className="text-sm font-medium text-gray-900">{mkt.annotatedOut.toLocaleString()}</span>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/client/video-packages')}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-left"
          >
            <div className="text-lg font-semibold text-gray-900 mb-4">Raw Videos</div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">In Market Place:</span>
                <span className="text-sm font-medium text-green-600">{mkt.rawVideoIn.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Not in Market Place:</span>
                <span className="text-sm font-medium text-gray-900">{mkt.rawVideoOut.toLocaleString()}</span>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
